import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { actionButtonStyle, declineActionBtn, loader } from "../styles/styles";
import { useTranslation } from "react-i18next";

const minimumNoticeStyle = {
  width: "100%",
  order: -1,
  fontSize: "13px",
  lineHeight: "160%",
  color: "rgba(62, 63, 76, 0.9)",
  backgroundColor: "rgba(255, 244, 229, 1)",
  borderRadius: "8px",
  padding: "8px 12px",
};

const ModalActions = (props) => {
  const {
    viewType,
    handleDeclineButtonClick,
    handleApproveJob,
    actionButtonLoader,
    handleCancelButtonClick,
    handleRequestPayment,
    isRequestPaymentDisabled,
    isProduct,
    hasUnavailable,
    canReturn,
    // The edit would leave the member with less than Keilot will charge for.
    belowMinimumAfterEdit,
    // The order as it stands is already under the payment minimum.
    belowMinimumOrder,
    orderMinimum,
    returnJobLoader,
    handleReturnForReapproval,
    awaitingMember,
    nextProductStatusLabel,
    advanceStatusLoader,
    handleAdvanceProductStatus,
    editMode,
    editFormId,
    updateOrderLoader,
    editBelowMinimum,
  } = props;
  const { t } = useTranslation("view-job-modal");
  // Product order with un-checked items → return to member for re-approval.
  const isReturnFlow = isProduct && hasUnavailable;

  // COMY1-292 — edit mode replaces the read-view actions with Cancel Job +
  // "Update & Notify Client" (the latter submits the shared editor form by id).
  if (editMode) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap="8px"
        width="100%"
      >
        <Button
          variant="contained"
          sx={{ ...declineActionBtn }}
          onClick={handleCancelButtonClick}
        >
          {t("cancel")}
        </Button>

        {editBelowMinimum && (
          <Typography sx={minimumNoticeStyle}>
            {t("below_minimum_after_edit", { amount: orderMinimum })}
          </Typography>
        )}
        <Button
          type="submit"
          form={editFormId}
          variant="contained"
          sx={{ ...actionButtonStyle }}
          disabled={updateOrderLoader || editBelowMinimum}
        >
          {t("edit.update_and_notify")}
          {updateOrderLoader && <CircularProgress size={20} sx={loader} />}
        </Button>
      </Box>
    );
  }

  return (
    <>
      {viewType === "pending" &&
        (awaitingMember ? (
          // Returned to the member: the supplier can cancel, or keep adjusting
          // availability and re-submit the update (enabled while at least one
          // item is still un-checked).
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap="8px"
            width="100%"
          >
            <Button
              variant="contained"
              sx={{ ...declineActionBtn }}
              onClick={handleDeclineButtonClick}
            >
              {t("cancel")}
            </Button>

            <Button
              variant="contained"
              sx={{ ...actionButtonStyle }}
              onClick={
                isReturnFlow ? handleReturnForReapproval : handleApproveJob
              }
              disabled={
                actionButtonLoader ||
                returnJobLoader ||
                // All items un-checked → can't return a fully-unavailable
                // order; only Cancel is allowed.
                (isReturnFlow && !canReturn)
              }
            >
              {/* All items re-checked (nothing unavailable) → the supplier can
                  fulfil the whole order, so offer Accept instead of Return. */}
              {isReturnFlow ? t("return_for_approval") : t("approve")}
              {(actionButtonLoader || returnJobLoader) && (
                <CircularProgress size={20} sx={loader} />
              )}
            </Button>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap="8px"
            width="100%"
          >
            {belowMinimumAfterEdit && (
              <Typography sx={minimumNoticeStyle}>
                {t("below_minimum_after_edit", { amount: orderMinimum })}
              </Typography>
            )}
            <Button
              variant="contained"
              sx={{
                ...declineActionBtn,
              }}
              onClick={handleDeclineButtonClick}
            >
              {t("decline")}
            </Button>

            <Button
              variant="contained"
              sx={{
                ...actionButtonStyle,
              }}
              onClick={
                isReturnFlow ? handleReturnForReapproval : handleApproveJob
              }
              disabled={
                actionButtonLoader ||
                returnJobLoader ||
                // All items un-checked → can't return a fully-unavailable
                // order; the supplier must Decline/Cancel instead.
                (isReturnFlow && !canReturn)
              }
            >
              {isReturnFlow ? t("return_for_approval") : t("approve")}
              {(actionButtonLoader || returnJobLoader) && (
                <CircularProgress size={20} sx={loader} />
              )}
            </Button>
          </Box>
        ))}

      {viewType === "active" && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap="8px"
          width="100%"
        >
          <Button
            variant="contained"
            sx={{
              ...declineActionBtn,
            }}
            onClick={handleCancelButtonClick}
          >
            {t("cancel")}
          </Button>

          {isProduct ? (
            // Products are already paid — advance to the next status instead.
            nextProductStatusLabel ? (
              <Button
                variant="contained"
                sx={{ ...actionButtonStyle }}
                onClick={handleAdvanceProductStatus}
                disabled={advanceStatusLoader}
              >
                {nextProductStatusLabel}
                {advanceStatusLoader && (
                  <CircularProgress size={20} sx={loader} />
                )}
              </Button>
            ) : null
          ) : (
            <>
              {/* Asking for a payment the member is not allowed to make would
                  only send them into a wall, so the request is refused here. */}
              {belowMinimumOrder && (
                <Typography sx={minimumNoticeStyle}>
                  {t("below_minimum_order", { amount: orderMinimum })}
                </Typography>
              )}
              <Button
                variant="contained"
                sx={{
                  ...actionButtonStyle,
                }}
                onClick={handleRequestPayment}
                disabled={
                  actionButtonLoader ||
                  isRequestPaymentDisabled ||
                  belowMinimumOrder
                }
              >
                {t("request_payment")}
                {actionButtonLoader && <CircularProgress size={20} sx={loader} />}
              </Button>
            </>
          )}
        </Box>
      )}

      {/* this is for phase1 */}
      {/* {viewType === "preview" && (
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Button
            variant="contained"
            sx={{
              ...actionButtonStyle,
            }}
            onClick={handleApproveJob}
            disabled={true}
          >
            {t("approve")}
            {actionButtonLoader && <CircularProgress size={20} sx={loader} />}
          </Button>
        </Box>
      )} */}
    </>
  );
};

export default ModalActions;
